import * as React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ReactPlayer from 'react-player';
import { BaseDialog } from '../dialog/BaseDialog';
import '../../pages/VideoDialog.css';


// Video Player Dialog Component
interface VideoPlayerDialogProps {
  open: boolean;
  onClose: () => void;
  videoName: string;
  videoUrl: string | null;
  onEnded: () => void;
  onPlay: () => void;
  onPause: () => void;
  onError: (error: any) => void;
  // 初回視聴のシーク(先送り)禁止と続きから再生用。useSeekGuard から渡される。
  playerRef: React.RefObject<ReactPlayer>;
  onReady: () => void;
  onProgress: (state: { playedSeconds: number }) => void;
}

export const VideoPlayerDialog: React.FC<VideoPlayerDialogProps> = ({
  open,
  onClose,
  videoName,
  videoUrl,
  onEnded,
  onPlay,
  onPause,
  onError,
  playerRef,
  onReady,
  onProgress
}) => {
  return (
    <BaseDialog 
      open={open} 
      onClose={onClose}
      aria-labelledby="video-dialog"
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="video-dialog" align="center">
        {videoName}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <div className="player-wrapper">
          <ReactPlayer
            ref={playerRef}
            className="react-player"
            width="100%"
            height="100%"
            url={videoUrl!}
            controls
            // iOS Safari でネイティブ全画面プレイヤー(onProgress でのシーク制御が
            // 効かない)に切り替わるのを防ぎ、インライン再生させる
            playsinline
            onEnded={onEnded}
            onPlay={onPlay}
            onPause={onPause}
            onReady={onReady}
            onProgress={onProgress}
            progressInterval={1000}
            onError={(e) => onError(e.target.error)}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload noplaybackrate',  // Disables download and playback rate controls
                }
              }
            }}
            onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
          />
        </div>
      </DialogContent>
    </BaseDialog>
  );
};

// Test Dialog Component
interface TestDialogProps {
  open: boolean;
  onClose: () => void;
  videoName: string;
  text: string;
  onTextChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isUpdate: boolean;
}

export const VideoTestDialog: React.FC<TestDialogProps> = ({
  open,
  onClose,
  videoName,
  text,
  onTextChange,
  onSubmit,
  isUpdate
}) => {
  const hasError = text.length > 200;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      aria-labelledby="test-dialog"
      PaperProps={{
        component: 'form',
        onSubmit,
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, fontWeight: 'bold' }}
        id="test-dialog"
        align="center"
      >
        {videoName + 'の小テスト'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <TextField
          autoFocus
          id="test-answer"
          name="answer"
          label="ここに動画を見た上で抱いた感想を記述してください(200文字以内)"
          required
          multiline
          fullWidth
          rows={14}
          value={text}
          onChange={onTextChange}
          error={hasError}
          helperText={hasError ? "200文字を超えています" : ""}
        />
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          variant="contained"
          sx={{
            borderRadius: '20px',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
          disabled={hasError && text.length > 0}
        >
          {isUpdate ? "更新する" : "提出する"}
        </Button>
      </DialogActions>
    </BaseDialog>
  );
};